/**
 * How each wallet speeds up a stuck transaction. The one copy is config/fee_bump_wallets.yml:
 * the server renders it as "Fee bumping in popular wallets" and puts the same data in
 * <script id="fee-bump-wallets"> for the wizard (app/modules/FeeBumpWallets.rb).
 */

/** What the wallet's fee field asks for. */
export type FeeUnit =
  | "fee_rate" // the replacement's fee rate (RBF)
  | "total_fee" // the replacement's total fee in sats (RBF)
  | "child_fee_rate" // the child's own fee rate (CPFP)
  | "effective_rate" // parent and child together (CPFP)
  | "child_fee" // the child's total fee in sats (CPFP)
  | "preset" // the wallet picks; the visitor chooses the fastest option
  | "none";

export interface WalletPath {
  method: "rbf" | "cpfp" | "none";
  /** the number(s) to show, e.g. Sparrow: a rate for its slider, or a fee to type */
  enter?: FeeUnit | FeeUnit[];
  /** 1-based step the number to enter goes under */
  enter_at?: number;
  /** the field takes whole numbers only */
  whole?: boolean;
  /** it only offers RBF for a transaction that signals it */
  signal_required?: boolean;
  steps?: string[];
  success?: string;
  note?: string;
}

export interface Wallet {
  id: string;
  name: string;
  formerly?: string;
  kind: "hardware" | "software" | "custodial";
  /** a hardware wallet's device name, for "connect your Trezor to Sparrow" */
  device?: string;
  /** shown with the Sparrow steps for this device (Ledger's Bitcoin app, COLDCARD's fee limit) */
  device_note?: string;
  /** extra search words */
  aliases?: string[];
  /** where it shows the transaction ID */
  txid: string;
  send: WalletPath;
  /** a sender's CPFP through their change output, when RBF isn't offered */
  send_cpfp?: WalletPath;
  receive: WalletPath;
  /** a hardware wallet whose own app can't do it: connect it to Sparrow */
  fallback?: "sparrow";
  /** custodial: where to ask */
  support?: string;
}

export interface FeeBumpData {
  wallets: Wallet[];
  /** connecting a hardware wallet to Sparrow, before Sparrow's own steps */
  sparrow_setup: { steps: string[] };
  /** "Something else" */
  other: Wallet;
  accelerator: { url: string; tx_url: string; note: string };
}

let cached: FeeBumpData | null | undefined;

export function feeBumpData(): FeeBumpData | null {
  if (cached !== undefined) return cached;
  try {
    const element = document.getElementById("fee-bump-wallets");
    cached = element ? (JSON.parse(element.textContent || "") as FeeBumpData) : null;
  } catch {
    cached = null;
  }
  return cached;
}

export const findWallet = (data: FeeBumpData, id: string | null): Wallet | null => {
  if (!id) return null;
  if (id === data.other.id) return data.other;
  return data.wallets.find((wallet) => wallet.id === id) ?? null;
};

/**
 * Step text can carry {txid}, {rate} (the first number to enter), {vout} and {amount} (the
 * chosen output, in BTC) for console commands, and {device} for the Sparrow set-up steps.
 */
export const fillStep = (text: string, values: Record<string, string>): string =>
  text.replace(/\{(txid|rate|vout|amount|device)\}/g, (token, name) => values[name] ?? token);

export const matchesSearch = (wallet: Wallet, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [wallet.name, wallet.formerly, ...(wallet.aliases ?? [])]
    .filter(Boolean)
    .some((name) => name.toLowerCase().includes(q));
};
